import { Component, Input, OnInit } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Message, MessageType } from 'src/app/features/message/message'
import { messageActions } from 'src/app/features/message/message.actions'
import { messageSelectors } from 'src/app/features/message/message.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Member } from '../../../member/member'
import { SerieProposal } from '../serie-proposal'
import { SerieProposalItemStatus } from '../serie-proposal-item/serie-proposal-item'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { RolesType } from 'src/app/features/role/role'
import { userRoleSelectors } from 'src/app/features/role/user-role/user-role.selectors'
import { roleSelectors } from 'src/app/features/role/role.selectors'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-messages-tab',
  templateUrl: './messages-tab.component.html',
  styleUrls: ['./messages-tab.component.scss']
})
export class SerieProposalMessagesTabComponent implements OnInit {
  @Input() serieProposalId: number
  public form: FormGroup
  public accessRights = AccessRights
  public productEntities$: Observable<Dictionary<Product>>
  public memberEntities$: Observable<Dictionary<Member>>
  public selectedLanguage$: Observable<string>
  public serieProposalItemStatus = SerieProposalItemStatus
  public externalMemo: string = null
  public member$: Observable<Member>
  public messages$: Observable<Message[]>
  public isLoading$: Observable<boolean>
  public serieProposalId$: Observable<number>
  public serieProposal$: Observable<SerieProposal>
  public serieProposal: SerieProposal

  public newMessage: string = ''

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private actions$: Actions
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.member$ = this.store.select(authSelectors.selectMember)

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)

    this.serieProposalId$ = this.serieProposalId
      ? of(this.serieProposalId)
      : this.store
          .select(serieProposalSelectors.selectIdRouteParam)
          .pipe(filter((id) => Boolean(id)))

    this.serieProposal$ = this.serieProposalId$.pipe(
      take(1),
      switchMap((id) =>
        this.store.select(serieProposalSelectors.selectById, id)
      )
    )

    this.serieProposal$
      .pipe(take(1))
      .subscribe((serieProposal) => (this.serieProposal = serieProposal))

    this.isLoading$ = this.store.select(messageSelectors.selectIsLoading)

    this.messages$ = this.store.select(messageSelectors.selectAll).pipe(
      untilDestroyed(this),

      map((messages) => {
        const serieProposalMessages = messages
          .filter((message) => message.serieProposalId == this.serieProposal.id)
          .sort((a, b) => (a.datePosted < b.datePosted ? -1 : 1))
        this.setMessageToRead(serieProposalMessages)
        return serieProposalMessages
      })
    )
  }

  public sendMessage() {
    if (this.newMessage.trim()) {
      this.member$.pipe(take(1)).subscribe((member) => {
        this.store.dispatch(
          messageActions.createOne({
            entity: {
              message: this.newMessage,
              messageType: MessageType.MESSAGE,
              senderPersonId: member.id,
              recipientPersonId: this.serieProposal.customerPersonId,
              serieProposalId: this.serieProposal.id
            }
          })
        )
      })

      this.actions$
        .pipe(
          ofType(messageActions.upsertMany),
          take(1),
          timeout(5000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.fetchMessages()
            this.newMessage = ''
          }
        })
    }
  }

  public fetchMessages() {
    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        messageActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              serieProposalId: this.serieProposal.id,
              $or: [
                { senderPersonId: member.id },
                { recipientPersonId: member.id }
              ]
            }
          })
        })
      )
    })
  }

  public isFrontendUserTheSender(
    message: Message,
    serieProposal: SerieProposal
  ): boolean {
    if (!serieProposal || !message) {
      return false
    }

    // If the message is sent by the frontend user, we assume that the recipient person id is null
    if (message.recipientPersonId === null) {
      return true
    }

    return false
  }

  private setMessageToRead(messages: Message[]) {
    this.member$.pipe(take(1)).subscribe((member) => {
      messages.map((message) => {
        if (
          !message.dateRead &&
          (message.recipientPersonId == member.id || !message.recipientPersonId)
        ) {
          this.store.dispatch(
            messageActions.updateOneById({
              id: message.id,
              params: {
                id: message.id,
                dateRead: moment()
              }
            })
          )
        }
      })
    })
  }
}
